import { useCallback } from 'react';
import { useFilterMapper } from '@nexelem/filters';
import {
  OrderingColumn,
  SimulationFilterDefinition,
  SimulationListFilterData,
  SimulationListResult,
  useGraphneAPI,
} from '@nexelem/graphne-api';
import { TypedFiltersPropsWithPagination } from '../../../FilteredGridPage/types';
import { simulationListFilterMapper } from '../SimulationList/components';

/** Grid column field -> Graphne SimulationFilterDefinition.order.column */
const orderingColumnMapper: Partial<Record<string, OrderingColumn>> = {
  creationDate: OrderingColumn.CreationDate,
  status: OrderingColumn.Status,
};

export const useSimulationListFetch = () => {
  const api = useGraphneAPI();
  const filterMapper = useFilterMapper(simulationListFilterMapper());

  return useCallback(
    async (
      filters: TypedFiltersPropsWithPagination<SimulationListFilterData>,
    ): Promise<{
      result: {
        data?: SimulationListResult['rows'];
        total: SimulationListResult['total'];
      };
    }> => {
      // Graphne list API uses `order` + top-level take/skip/rows, not Nexelem's `ordering` + result wrapper.
      const simulationFilterDefinition: SimulationFilterDefinition = {
        take: filters.take ?? 20,
        skip: filters.skip ?? 0,
        data: filterMapper(filters.data),
      };

      if (filters.ordering?.column) {
        const mappedColumn = orderingColumnMapper[filters.ordering.column];
        if (mappedColumn) {
          simulationFilterDefinition.order = {
            column: mappedColumn,
            asc: filters.ordering.asc,
          };
        }
      }

      const response = await api.postGraphneSimulationList({ simulationFilterDefinition });

      return {
        result: {
          data: response.rows,
          total: response.total,
        },
      };
    },
    [api, filterMapper],
  );
};
